import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchLogGet } from '../../redux/slices/logGetSlice'
import CMS from '../../components/cms/CMS'
import PageBanner from '../../components/general/PageBanner'
import { useTranslation } from 'react-i18next'
import {
    Table, Badge, Button, Input, Select, DatePicker, Space, Modal,
    Tabs, Tag, Tooltip, Card, Typography, Row, Col, message, Spin
} from 'antd'
import {
    SearchOutlined, EyeOutlined, ReloadOutlined,
    FilterOutlined, InfoCircleOutlined, ClearOutlined
} from '@ant-design/icons'
import moment from 'moment'
import JSONPretty from 'react-json-pretty'
import 'react-json-pretty/themes/monikai.css'

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

const LogPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Redux state
    const allLogs = useSelector((state) => state.logGet.data);
    const status = useSelector((state) => state.logGet.status);

    // Local state
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    const [filters, setFilters] = useState({
        serviceName: null,
        statusCode: null,
        dateRange: null,
        search: '',
        sortField: null,
        sortOrder: null
    });

    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);

    // Load logs from Redux
    useEffect(() => {
        dispatch(fetchLogGet());
    }, [dispatch]);

    // When logs or filters change, apply filters and update filtered logs
    useEffect(() => {
        if (!allLogs) return;

        let result = [...allLogs];

        // Apply filters
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            result = result.filter(log =>
                (log.serviceName && log.serviceName.toLowerCase().includes(searchLower)) ||
                (log.process && log.process.toLowerCase().includes(searchLower)) ||
                (log.message && log.message.toLowerCase().includes(searchLower)) ||
                (log.ip && log.ip.toLowerCase().includes(searchLower))
            );
        }

        if (filters.serviceName) {
            result = result.filter(log => log.serviceName === filters.serviceName);
        }

        if (filters.statusCode) {
            result = result.filter(log => log.statusCode === filters.statusCode);
        }

        if (filters.dateRange && filters.dateRange.length === 2) {
            const startDate = filters.dateRange[0].startOf('day');
            const endDate = filters.dateRange[1].endOf('day');

            result = result.filter(log => {
                const logDate = moment(log.createdAt);
                return logDate.isBetween(startDate, endDate, null, '[]');
            });
        }

        // Apply sorting
        if (filters.sortField && filters.sortOrder) {
            const { sortField, sortOrder } = filters;
            result.sort((a, b) => {
                let valueA = a[sortField];
                let valueB = b[sortField];

                // Handle date comparison
                if (sortField === 'createdAt') {
                    valueA = moment(valueA);
                    valueB = moment(valueB);
                }

                // Handle nulls
                if (valueA === null) return sortOrder === 'ascend' ? -1 : 1;
                if (valueB === null) return sortOrder === 'ascend' ? 1 : -1;

                // Compare values
                if (valueA < valueB) return sortOrder === 'ascend' ? -1 : 1;
                if (valueA > valueB) return sortOrder === 'ascend' ? 1 : -1;
                return 0;
            });
        }

        // Update pagination
        const total = result.length;
        const start = (pagination.current - 1) * pagination.pageSize;
        const end = start + pagination.pageSize;
        const paginatedResult = result.slice(start, end);

        setFilteredLogs(paginatedResult);
        setPagination(prev => ({ ...prev, total }));

    }, [allLogs, filters, pagination.current, pagination.pageSize]);

    // Status code colors
    const getStatusColor = (statusCode) => {
        if (!statusCode) return 'default';
        if (statusCode >= 200 && statusCode < 300) return 'success';
        if (statusCode >= 300 && statusCode < 400) return 'processing';
        if (statusCode >= 400 && statusCode < 500) return 'warning';
        if (statusCode >= 500) return 'error';
        return 'default';
    };

    // Handle table change (pagination, filters, sorter)
    const handleTableChange = (newPagination, _, sorter) => {
        setPagination({
            ...newPagination,
            current: newPagination.current || 1
        });

        if (sorter && sorter.field) {
            setFilters(prev => ({
                ...prev,
                sortField: sorter.field,
                sortOrder: sorter.order
            }));
        }
    };

    // Handle filter change
    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            serviceName: null,
            statusCode: null,
            dateRange: null,
            search: '',
            sortField: null,
            sortOrder: null
        });
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Refresh data
    const refreshLogs = () => {
        dispatch(fetchLogGet());
        message.success(t('logs_refreshed'));
    };

    // View log details
    const viewLogDetails = (log) => {
        setSelectedLog(log);
        setDetailModalVisible(true);
    };

    // Extract unique service names for filter dropdown
    const serviceNames = useMemo(() => {
        if (!allLogs) return [];
        return [...new Set(allLogs.map(log => log.serviceName).filter(Boolean))];
    }, [allLogs]);

    // Extract unique status codes for filter dropdown
    const statusCodes = useMemo(() => {
        if (!allLogs) return [];
        return [...new Set(allLogs.map(log => log.statusCode).filter(Boolean))];
    }, [allLogs]);

    // Format the date
    const formatDate = (dateStr) => {
        return moment(dateStr).format('YYYY-MM-DD HH:mm:ss');
    };

    // Parse JSON for result field
    const parseJSON = (jsonStr) => {
        try {
            return jsonStr ? JSON.parse(jsonStr) : null;
        } catch (e) {
            return jsonStr;
        }
    };

    // Table columns
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
            sorter: true
        },
        {
            title: t('status'),
            dataIndex: 'statusCode',
            key: 'statusCode',
            width: 100,
            render: (statusCode) => (
                <Badge
                    status={getStatusColor(statusCode)}
                    text={statusCode || 'N/A'}
                />
            ),
            sorter: true,
        },
        {
            title: t('service'),
            dataIndex: 'serviceName',
            key: 'serviceName',
            width: 150,
            render: (text) => text || 'N/A',
            sorter: true,
        },
        {
            title: t('process'),
            dataIndex: 'process',
            key: 'process',
            width: 150,
            render: (text) => text || 'N/A',
            sorter: true,
        },
        {
            title: t('message'),
            dataIndex: 'message',
            key: 'message',
            ellipsis: true,
            render: (text) => text || 'N/A',
        },
        {
            title: 'IP',
            dataIndex: 'ip',
            key: 'ip',
            width: 120,
        },
        {
            title: t('date'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (text) => formatDate(text),
            sorter: true,
        },
        {
            title: t('actions'),
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <Tooltip title={t('view_details')}>
                    <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => viewLogDetails(record)}
                    />
                </Tooltip>
            ),
        },
    ];

    return (
        <CMS>
            <PageBanner
                title={t("saved_log")}
                description={t("saved_log_desc")}
            />

            <Card>
                {/* Filters */}
                <div className="log-filters" style={{ marginBottom: 16 }}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6} lg={6}>
                            <Input
                                placeholder={t("search")}
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                prefix={<SearchOutlined />}
                                allowClear
                            />
                        </Col>
                        <Col xs={24} sm={12} md={6} lg={5}>
                            <Select
                                placeholder={t("service")}
                                value={filters.serviceName}
                                onChange={(value) => handleFilterChange('serviceName', value)}
                                style={{ width: '100%' }}
                                allowClear
                            >
                                {serviceNames.map(name => (
                                    <Option key={name} value={name}>{name}</Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={6} lg={5}>
                            <Select
                                placeholder={t("status_code")}
                                value={filters.statusCode}
                                onChange={(value) => handleFilterChange('statusCode', value)}
                                style={{ width: '100%' }}
                                allowClear
                            >
                                {statusCodes.map(code => (
                                    <Option key={code} value={code}>{code}</Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={6} lg={6}>
                            <RangePicker
                                style={{ width: '100%' }}
                                onChange={(dates) => handleFilterChange('dateRange', dates)}
                                value={filters.dateRange}
                            />
                        </Col>
                        <Col xs={24} sm={24} md={24} lg={2}>
                            <Button
                                type="default"
                                onClick={resetFilters}
                                icon={<ClearOutlined />}
                                style={{ width: '100%' }}
                            >
                                {t("reset")}
                            </Button>
                        </Col>
                    </Row>
                </div>

                {/* Log Table */}
                {status === 'loading' ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin size="large" />
                        <p style={{ marginTop: '10px' }}>{t('loading_logs')}</p>
                    </div>
                ) : status === 'failed' ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Text type="danger">{t('failed_fetch_logs')}</Text>
                        <Button
                            onClick={refreshLogs}
                            icon={<ReloadOutlined />}
                            style={{ marginLeft: 8 }}
                        >
                            {t('retry')}
                        </Button>
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={filteredLogs}
                        rowKey="id"
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showTotal: (total) => t('total_logs', { count: total })
                        }}
                        onChange={handleTableChange}
                        loading={status === 'loading'}
                        scroll={{ x: 'max-content' }}
                    />
                )}
            </Card>

            {/* Log Detail Modal */}
            <Modal
                title={
                    <Space>
                        <InfoCircleOutlined />
                        {t("log_details")}
                        {selectedLog &&
                            <Tag color={getStatusColor(selectedLog.statusCode)}>
                                {selectedLog.statusCode}
                            </Tag>
                        }
                    </Space>
                }
                visible={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                width={800}
                footer={[
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>
                        {t("close")}
                    </Button>
                ]}
            >
                {selectedLog && (
                    <Tabs defaultActiveKey="1">
                        <TabPane tab={t("general")} key="1">
                            <Row gutter={[16, 8]}>
                                <Col span={8}><Text strong>{t("id")}:</Text></Col>
                                <Col span={16}>{selectedLog.id}</Col>

                                <Col span={8}><Text strong>{t("service")}:</Text></Col>
                                <Col span={16}>{selectedLog.serviceName || 'N/A'}</Col>

                                <Col span={8}><Text strong>{t("status_code")}:</Text></Col>
                                <Col span={16}>
                                    <Badge
                                        status={getStatusColor(selectedLog.statusCode)}
                                        text={selectedLog.statusCode || 'N/A'}
                                    />
                                </Col>

                                <Col span={8}><Text strong>{t("process")}:</Text></Col>
                                <Col span={16}>{selectedLog.process || 'N/A'}</Col>

                                <Col span={8}><Text strong>{t("message")}:</Text></Col>
                                <Col span={16}>{selectedLog.message || 'N/A'}</Col>

                                <Col span={8}><Text strong>{t("ip")}:</Text></Col>
                                <Col span={16}>{selectedLog.ip}</Col>

                                <Col span={8}><Text strong>{t("user_id")}:</Text></Col>
                                <Col span={16}>{selectedLog.userId || 'N/A'}</Col>

                                <Col span={8}><Text strong>{t("date")}:</Text></Col>
                                <Col span={16}>{formatDate(selectedLog.createdAt)}</Col>
                            </Row>
                        </TabPane>

                        <TabPane tab={t("result")} key="2">
                            {selectedLog.result ? (
                                <JSONPretty
                                    id="json-pretty"
                                    data={parseJSON(selectedLog.result)}
                                    style={{ maxHeight: '400px', overflow: 'auto' }}
                                />
                            ) : (
                                <Text type="secondary">{t("no_result_data")}</Text>
                            )}
                        </TabPane>
                    </Tabs>
                )}
            </Modal>
        </CMS>
    );
}

export default LogPage;